import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { usePortalLogin, socialConnections } from '../../hooks/usePortalLogin';

export function AdminLogin() {
  const { authConfigured, isPending, login, loginWithConnection } = usePortalLogin({
    portal: 'admin',
    returnTo: '/admin',
  });
  const hasSocialLogins = socialConnections.length > 0;
  const resolveConnectionLabel = (connection: string) => {
    const value = connection.toLowerCase();
    if (value.includes('google')) return 'Continue with Google';
    if (value.includes('github')) return 'Continue with GitHub';
    if (value.includes('linkedin')) return 'Continue with LinkedIn';
    if (value.includes('microsoft') || value.includes('azure')) return 'Continue with Microsoft';
    return `Continue with ${connection}`;
  };

  return (
    <section
      aria-labelledby="admin-login-heading"
      className="relative min-h-screen pt-16 flex items-center justify-center p-4 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]" />
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <header className="text-center mb-8">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-gold-gradient items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-black" />
          </div>
          <h1 id="admin-login-heading" className="mb-2 text-gold-gradient">
            Admin Access
          </h1>
          <p className="text-gray-400">Secure controls for Lighthouse staff</p>
        </header>

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Single Sign-On required</CardTitle>
            <CardDescription className="text-gray-400">
              MFA is enforced for every admin action. Continue to Auth0 to verify your identity.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-gray-300">
            {hasSocialLogins && (
              <div className="space-y-2">
                {socialConnections.map((connection: string) => (
                  <Button
                    key={connection}
                    type="button"
                    variant="outline"
                    onClick={() => void loginWithConnection(connection)}
                    disabled={isPending}
                    className="w-full border-white/20 text-white hover:bg-white/10"
                  >
                    {resolveConnectionLabel(connection)}
                  </Button>
                ))}
              </div>
            )}
            <Button
              type="button"
              onClick={() => void login()}
              disabled={isPending}
              className="w-full bg-gold-gradient text-black hover:brightness-110"
            >
              {isPending ? 'Preparing secure login...' : 'Continue to Auth0'}
            </Button>
            {!authConfigured && (
              <p className="text-xs text-gray-500">
                Auth0 isn&apos;t configured locally, so we&apos;ll simulate access and drop you into the admin console.
              </p>
            )}
            <p className="text-xs text-gray-400">
              Need access? Contact the Lighthouse platform team so they can assign the proper roles within Auth0.
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
